import { useState } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';

import { ZERO } from '@/constants';
import { Button, Tab, Tabs, Typography } from '@/ui';

import OpsCompanyInfo from './OpsCompanyInfo';
import OpsCompanyStats from './OpsCompanyStats';

import type { SyntheticEvent } from 'react';

import CollapsedCompanyHeader from '@/features/companyDashboard/common/CollapsedCompanyHeader';
import { useDownloadUsersCsv } from '@/features/companyDashboard/common/downloadUsersCsv';
import { useCompany, useCompanyMembers } from '@/hooks/useCompany';

const OpsCompanyLayout = () => {
  const { companyId = '' } = useParams();

  const navigate = useNavigate();
  const [tab, setTab] = useState(ZERO);
  const [isCompanyPanelCollapsed, setIsCompanyPanelCollapsed] = useState(false);

  const { companyData, isCompanyLoading } = useCompany(companyId);
  const { download, isDownloading } = useDownloadUsersCsv(
    'ops',
    companyData?.name,
  );

  const { data: upcomingData, isLoading: upcomingLoading } = useCompanyMembers(
    companyId,
    true,
    1,
    1,
  );

  const { data: pastData, isLoading: pastLoading } = useCompanyMembers(
    companyId,
    false,
    1,
    1,
  );

  const onClick = (path: string) => () => {
    navigate(path, { state: { resetMembersPage: true } });
  };

  const onChange = (_: SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  const onDownloadCSV = async () => {
    try {
      await download();
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  if (isCompanyLoading || !companyData) {
    return null;
  }

  const isCountsLoading = pastLoading || upcomingLoading;
  // Content gutter for Members and the members table. The collapsed header sets
  // its own, intentionally narrower, gutter.
  const contentGutter = isCompanyPanelCollapsed ? 'px-20' : 'px-12';

  return (
    <div className="flex-1 gap-0 lg:flex lg:flex-row">
      <OpsCompanyInfo
        company={companyData}
        isCollapsed={isCompanyPanelCollapsed}
        onCollapsedChange={setIsCompanyPanelCollapsed}
      />
      <div className="w-full min-w-0 flex-1 bg-white">
        {isCompanyPanelCollapsed ? (
          <CollapsedCompanyHeader
            company={companyData}
            stats={<OpsCompanyStats isCompact />}
          />
        ) : (
          <div className={contentGutter}>
            <OpsCompanyStats />
          </div>
        )}
        <div className={contentGutter}>
          <div className="flex items-center justify-between pt-10">
            <Typography variant="h4">Members</Typography>
            <Button
              disabled={isDownloading}
              variant="outlined"
              onClick={onDownloadCSV}
            >
              {isDownloading ? 'Downloading...' : 'Download Users CSV'}
            </Button>
          </div>
          <Tabs value={tab} onChange={onChange}>
            <Tab
              label={`Current members ${
                !isCountsLoading ? `(${upcomingData?.total || 0})` : ''
              }`}
              onClick={onClick(`/coach/manage/companies/${companyId}/current`)}
            />
            <Tab
              label={`Past members ${!isCountsLoading ? `(${pastData?.total || 0})` : ''}`}
              onClick={onClick(`/coach/manage/companies/${companyId}/past`)}
            />
          </Tabs>
          <div className="w-full overflow-x-auto pb-4">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpsCompanyLayout;
